import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { getEstudo } from "@/lib/estudo";
import { BancaGrid } from "@/components/BancaGrid";
import { TrendingUp, TrendingDown, BarChart3, Loader2, Landmark } from "lucide-react";

const Select = ({ value, onChange, options, testid, placeholder }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full min-w-0 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white focus:border-emerald-500 focus:outline-none sm:w-auto">
    <option value="Todas">{placeholder}</option>
    {options.map((o) => <option key={o} value={o}>{o}</option>)}
  </select>
);

export default function IncidenciaPage() {
  const est = getEstudo();
  const [banca, setBanca] = useState(est.banca || "Todas");
  const [concurso] = useState(est.concurso || "Todas");
  const [disciplina, setDisciplina] = useState("Todas");
  const [disciplinas, setDisciplinas] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/questions/filters").then((r) => setDisciplinas(r.data.disciplinas || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/analytics/incidence", { params: { banca, disciplina, concurso } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [banca, disciplina, concurso]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="incidencia-page" className="mx-auto max-w-4xl space-y-5 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-amber-500/15 text-amber-400"><BarChart3 className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-white">Análise de Incidência</h1>
          <p className="text-sm text-slate-400">O que mais e o que menos cai — CEBRASPE e CESGRANRIO.</p>
        </div>
      </div>

      <div>
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Banca</p>
        <BancaGrid value={banca} onSelect={setBanca} />
      </div>
      <div className="w-full min-w-0">
        <Select value={disciplina} onChange={setDisciplina} options={disciplinas} testid="incidencia-disciplina" placeholder="Todas as disciplinas" />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-emerald-400" /></div>
      ) : !data || data.total_analisado === 0 ? (
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-10 text-center text-slate-400" data-testid="incidencia-empty">
          {data?.base || "Sem dados suficientes para esta seleção."}
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-500">{data.base} · {data.total_analisado.toLocaleString("pt-BR")} questões analisadas</p>

          <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2">
            <div data-testid="incidencia-mais" className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-emerald-500/30 bg-slate-900 p-5">
              <div className="mb-3 flex items-center gap-2 text-emerald-400"><TrendingUp className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Mais cobrados</span></div>
              <div className="space-y-2">
                {data.mais_caem.map((r, i) => (
                  <div key={r.assunto} className="flex min-w-0 items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 break-words text-slate-200"><b className="text-slate-500">{i + 1}.</b> {r.assunto} <span className="text-xs text-slate-500">({r.disciplina})</span></span>
                    <span className="shrink-0 font-mono-stats text-emerald-400">{r.questoes} · {r.percentual}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div data-testid="incidencia-menos" className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-700 bg-slate-900 p-5">
              <div className="mb-3 flex items-center gap-2 text-slate-400"><TrendingDown className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Menos cobrados</span></div>
              <div className="space-y-2">
                {data.menos_caem.length === 0 ? (
                  <p className="text-xs text-slate-500">Poucos assuntos nesta seleção para gerar um ranking inverso.</p>
                ) : data.menos_caem.map((r) => (
                  <div key={r.assunto} className="flex min-w-0 items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 break-words text-slate-300">{r.assunto} <span className="text-xs text-slate-500">({r.disciplina})</span></span>
                    <span className="shrink-0 font-mono-stats text-slate-400">{r.questoes} · {r.percentual}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid min-w-0 grid-cols-1 gap-4 md:grid-cols-2" data-testid="incidencia-por-banca">
            {data.por_banca.map((b) => (
              <div key={b.banca} className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 p-5">
                <div className="mb-3 flex items-center gap-2 text-slate-200"><Landmark className="h-4 w-4 text-amber-400" /><span className="text-xs font-bold uppercase tracking-wide">Mais cobrado — {b.banca}</span></div>
                {b.top.length === 0 ? <p className="text-xs text-slate-500">Sem dados.</p> : b.top.map((t, i) => (
                  <div key={t.assunto} className="flex min-w-0 items-center justify-between gap-2 text-sm">
                    <span className="min-w-0 flex-1 break-words text-slate-300"><b className="text-slate-500">{i + 1}.</b> {t.assunto}</span>
                    <span className="shrink-0 font-mono-stats text-emerald-400">{t.questoes}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
